import React from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid/v1';
import { Bert } from 'meteor/themeteorchef:bert';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Modal,
  Paper,
  Stack,
  Step,
  Stepper,
  StepLabel,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Pagination
} from '@mui/material';
import { isEmpty } from 'lodash';

import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Loading from '../Loading';
import useTimeStore from '../ZustandProvider';

const ISLComponent = ({ MapProps, isFullScreen, isMobile }) => {
  const { ipsOfIsl } = MapProps.map.settingsAdvancedMap;
  const drawerWidth = useTimeStore(state => state.drawerWidth);

  const [activeStep, setActiveStep] = React.useState(0);
  const [answerFunction, setAnswerFunction] = React.useState({});
  const [firstAnswer, setFirstAnswer] = React.useState([]);
  const [openModal, setOpenModal] = React.useState(false);
  const [isMapSelected, setIsMapSelected] = React.useState(false);
  const [openViewMap, setOpenViewMap] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [tablePage, setTablePage] = React.useState(0);
  const [valueToShow, setValueToShow] = React.useState({});
  const [viewType, setViewType] = React.useState('Imagenes');
  const itemsPerPage = isFullScreen || drawerWidth >= 50 ? 8 : 4;
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const steps = 2;
  const rowsPerPage = 10;
  const stepsProperties = ['Entrada', 'Salida'];

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: '#2A3F90',
      color: theme.palette.common.white
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14
    }
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    },
    '&:last-child td, &:last-child th': {
      border: 0
    }
  }));

  const StyledTable = styled(Table)(() => ({
    border: `1px solid #2A3F90`
  }));

  const callToBackToDoFunction = async () => {
    const nuevoObjeto = {};
    Object.keys(valueToShow.variables).forEach(key => {
      nuevoObjeto[key] = valueToShow.variables[key].input;
    });

    let bodyConfiguration = {};
    if (valueToShow.type === 'ISL') {
      bodyConfiguration = {
        disk: 'data3',
        udf_selected: valueToShow.path,
        variables: nuevoObjeto,
        get_map: isMapSelected
      };
    } else if (valueToShow.type === 'Airflow') {
      bodyConfiguration = {
        function: valueToShow.path
      };
    }

    const response = await fetch(valueToShow.url, {
      method: 'POST',
      body: JSON.stringify(bodyConfiguration),
      headers: { 'Content-Type': 'application/json' },
      mode: 'cors',
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    });
    const respuesta = await response.json();
    setIsLoading(false);
    if (respuesta.status === 'OK') {
      setAnswerFunction(respuesta.data);
    } else {
      Bert.alert(respuesta.data, 'danger');
    }
  };

  const handleNext = () => {
    if (activeStep === steps - 1) {
      setOpenModal(false);
      setActiveStep(0);
    } else if (activeStep === 0) {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
      setIsLoading(true);
      callToBackToDoFunction();
    } else {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
    }
  };
  const handleChangeTablePage = (event, newPage) => {
    setTablePage(newPage);
  };
  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeInput = (event, key, variable) => {
    const { value } = event.target;
    const newValue = { ...valueToShow };
    newValue.variables[key].input = value;
    if (variable.type === 'String') {
      setValueToShow(newValue);
    } else if (variable.type === 'Number' && (Number.isNaN(value) || value <= 0)) {
      setValueToShow(newValue);
    } else {
      setValueToShow(newValue);
    }
  };

  const renderInput = (key, variable) => {
    switch (variable.type) {
      case 'String':
        return (
          <TextField
            id="outlined-required"
            label={variable.variable_name}
            size="small"
            value={variable.input}
            onChange={event => handleChangeInput(event, key, variable)}
            InputProps={{
              endAdornment: (
                <Tooltip title={variable.explanation} placement="top">
                  <IconButton style={{ color: '#2A3F90' }}>
                    <HelpOutlineIcon />
                  </IconButton>
                </Tooltip>
              )
            }}
            fullWidth
          />
        );
      case 'Number':
        return (
          <TextField
            id="outlined-required"
            label={variable.variable_name}
            size="small"
            value={variable.input}
            onChange={event => handleChangeInput(event, key, variable)}
            mb={2}
            fullWidth
          />
        );

      default:
        return (
          <TextField
            id="outlined-required"
            label={variable.variable_name}
            size="small"
            value={variable.input}
            onChange={event => handleChangeInput(event, key, variable)}
            mb={2}
            fullWidth
          />
        );
    }
  };

  const body = (
    <Box backgroundColor="white" height="100%" width="100%">
      <Box display="flex" height="10%" width="100%" alignItems="center" mr={5}>
        <Grid container direction="row" justifyContent="space-around">
          <Grid item xs={3} sx={{ justifyContent: 'space-around', display: 'flex' }}>
            <Typography variant="h6" fontWeight="bold">
              {valueToShow.name}
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Button
              disabled={activeStep === 0}
              sx={{
                color: '#2A3F90'
              }}
              onClick={handleBack}
            >
              Back
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <Stepper activeStep={1} alternativeLabel sx={{ width: '100%' }}>
                {stepsProperties.map(label => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Grid>
          <Grid
            item
            xs={1}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
          >
            <Button
              variant="contained"
              sx={{
                color: 'white',
                backgroundColor: '#2A3F90'
              }}
              onClick={handleNext}
            >
              {activeStep === steps - 1 ? 'Finish' : 'Next'}
            </Button>
            <IconButton
              variant="outlined"
              onClick={() => {
                setOpenModal(false);
                setActiveStep(0);
              }}
              sx={{ marginLeft: '16px', color: '#2A3F90' }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      <Box height="90%" width="100%" ml={3} mr={3} justifyContent="center">
        {activeStep === 0 && !isEmpty(valueToShow) && (
          <Box ml={3} mr={3}>
            {!isEmpty(valueToShow.explanation) && (
              <Typography variant="body1" m={1}>
                {valueToShow.explanation}
              </Typography>
            )}
            <Typography variant="h6" fontWeight="bold" m={1}>
              Variables de entrada
            </Typography>
            <Box width="40%" m={2} display="flex" flexDirection="column">
              {Object.keys(valueToShow.variables).map(key => (
                <>
                  {valueToShow.variables[key].optional !== true && (
                    <Box mb={2} key={key}>
                      {renderInput(key, valueToShow.variables[key])}
                    </Box>
                  )}
                </>
              ))}
            </Box>
            {Object.values(valueToShow.variables).some(variable => variable.optional) && (
              <Box width="40%" m={3} display="flex" flexDirection="column">
                <Typography variant="body2" mb={2}>
                  Variables opcionales
                </Typography>
                {Object.keys(valueToShow.variables).map(key => (
                  <React.Fragment key={key}>
                    {valueToShow.variables[key].optional === true && (
                      <Box mb={2}>{renderInput(key, valueToShow.variables[key])}</Box>
                    )}
                  </React.Fragment>
                ))}
              </Box>
            )}
            {valueToShow.type === 'ISL' && (
              <Box display="row" justifyContent="space-evenly" fullwidth="true" mb={2}>
                <Checkbox
                  checked={isMapSelected}
                  onChange={() => {
                    setIsMapSelected(!isMapSelected);
                  }}
                  sx={{
                    color: '#2A3F90',
                    '&.Mui-checked': {
                      color: '#2A3F90'
                    }
                  }}
                  value="isMapSelected"
                />
                Mostrar mapa
              </Box>
            )}
          </Box>
        )}
        {activeStep === 1 && (
          <Box
            ml={3}
            mr={3}
            flexGrow={1}
            display="flex"
            flexDirection="column"
            height="100%"
            width="95%"
          >
            {isLoading ? (
              <Loading />
            ) : (
              <>
                {isMapSelected && !isEmpty(answerFunction.map) && (
                  <>
                    <Box mb={2} flexGrow={1} display="flex" flexDirection="column">
                      <Grid container spacing={2} alignItems="center">
                        <Grid item>
                          <IconButton
                            onClick={() => {
                              setOpenViewMap(!openViewMap);
                            }}
                          >
                            {openViewMap ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                          </IconButton>
                        </Grid>
                        <Grid item>
                          <Typography variant="body1">Ver mapa</Typography>
                        </Grid>
                        <Grid item xs>
                          <Divider sx={{ height: 1, backgroundColor: 'grey' }} />
                        </Grid>
                      </Grid>
                      {openViewMap && (
                        <Box
                          sx={{
                            flexGrow: 1,
                            mt: 2,
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                        >
                          <iframe
                            title="WebPage"
                            src={answerFunction.map}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                          />
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );

  const addPathToUrl = (urlProp, type) => {
    let url = urlProp;
    const pathISL = 'MG/isl/exec_isl';
    const pathAirflow = 'MG/airflow/exec_airflow';
    if (!url.endsWith('/')) {
      url += '/';
    }
    if (type === 'Airflow') {
      return url + pathAirflow;
    }
    if (type === 'ISL') {
      return url + pathISL;
    }
    return '';
  };

  React.useEffect(() => {
    const findAnswer = async item => {
      let bodyConfiguration = {};
      if (item.type === 'ISL') {
        bodyConfiguration = {
          disk: 'data3',
          functions_availables: true
        };
      } else if (item.type === 'Airflow') {
        bodyConfiguration = {
          function: 'FirstRequest'
        };
      }
      const response = await fetch(addPathToUrl(item.url, item.type), {
        method: 'POST',
        body: JSON.stringify(bodyConfiguration),
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer'
      });
      const respuesta = await response.json();

      if (!isEmpty(respuesta) && respuesta.status === 'OK') {
        return respuesta.data.map(elements => {
          return { ...elements, url: addPathToUrl(item.url, item.type), type: item.type };
        });
      }
      return [];
    };

    const fetchAllAnswers = async () => {
      const allAnswers = await Promise.all(ipsOfIsl.map(findAnswer));
      const flattenedAnswers = allAnswers.flat();
      setFirstAnswer(flattenedAnswers);
    };

    fetchAllAnswers();
  }, []);

  return (
    <>
      <Box
        sx={{
          overflow: 'hidden',
          mr: '5%',
          ml: '5%',
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}
      >
        {!isEmpty(firstAnswer) && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                '& > *': {
                  m: 1
                }
              }}
            >
              <ButtonGroup variant="contained" aria-label="outlined primary button group">
                <Button
                  style={{
                    fontSize: 11,
                    backgroundColor: viewType === 'Imagenes' ? '#2A3F90' : 'inherit',
                    color: viewType === 'Imagenes' ? 'white' : 'inherit',
                    size: 'small',
                    border: '1px solid #2A3F90'
                  }}
                  onClick={() => setViewType('Imagenes')}
                  disabled={viewType === 'Imagenes'}
                >
                  Imagenes
                </Button>
                <Button
                  style={{
                    fontSize: 11,
                    backgroundColor: viewType === 'Listado' ? '#2A3F90' : 'inherit',
                    color: viewType === 'Listado' ? 'white' : 'inherit',
                    size: 'small',
                    border: '1px solid #2A3F90'
                  }}
                  onClick={() => setViewType('Listado')}
                  disabled={viewType === 'Listado'}
                >
                  Listado
                </Button>
              </ButtonGroup>
            </Box>
            {viewType === 'Imagenes' && (
              <>
                <Grid container spacing={1} justifyContent="center">
                  {firstAnswer.slice(startIndex, endIndex).map(item => (
                    <Grid
                      item
                      key={uuid()}
                      xs={12}
                      sm={itemsPerPage === 4 ? 6 : 3}
                      md={itemsPerPage === 4 ? 6 : 3}
                      lg={itemsPerPage === 4 ? 6 : 3}
                    >
                      {isMobile === true ? (
                        <Card
                          sx={{
                            display: 'flex',
                            borderRadius: 4,
                            margin: 'auto',
                            mb: 2
                            // maxWidth: 400
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <CardContent
                              sx={{ flex: '1 0 auto', maxHeight: 170, overflowY: 'auto' }}
                            >
                              <Typography component="div" variant="h6">
                                {item.name}
                              </Typography>
                              <Typography
                                color="text.secondary"
                                component="div"
                                variant="subtitle1"
                              >
                                {item.explanation}
                              </Typography>
                            </CardContent>
                            <Box sx={{ alignItems: 'center', display: 'flex', pl: 1, pb: 1 }}>
                              <Button
                                size="small"
                                style={{ backgroundColor: '#2A3F90', color: 'white', margin: 0 }}
                                onClick={() => {
                                  const newVariables = {};
                                  Object.keys(item.variables).forEach(key => {
                                    const variable = item.variables[key];
                                    newVariables[key] = {
                                      ...variable,
                                      input: variable.example
                                    };
                                  });
                                  const newItem = { ...item, variables: newVariables };
                                  setValueToShow(newItem);
                                  setOpenModal(true);
                                }}
                              >
                                Open
                              </Button>
                            </Box>
                          </Box>
                          <CardMedia
                            sx={{
                              flex: '0 0 auto',
                              height: 180,
                              width: 180,
                              maxWidth: 200,
                              backgroundSize: 'cover'
                            }}
                            image={item.image}
                            title={item.name}
                          />
                        </Card>
                      ) : (
                        <Card sx={{ maxWidth: 200, borderRadius: 4 }}>
                          <CardMedia
                            sx={{ height: 180, width: 180 }}
                            image={item.image}
                            title={item.name}
                          />
                          <CardContent sx={{ flex: '1 0 auto', maxHeight: 100, overflowY: 'auto' }}>
                            <Typography gutterBottom variant="h5" component="div">
                              {item.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              style={{
                                color: '#000000',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {item.explanation}
                            </Typography>
                          </CardContent>
                          <CardActions sx={{ justifyContent: 'center', padding: 0 }}>
                            <Button
                              size="small"
                              style={{ backgroundColor: '#2A3F90', color: 'white', margin: 0 }}
                              onClick={() => {
                                const newVariables = {};
                                Object.keys(item.variables).forEach(key => {
                                  const variable = item.variables[key];
                                  newVariables[key] = {
                                    ...variable,
                                    input: variable.example
                                  };
                                });
                                const newItem = { ...item, variables: newVariables };
                                setValueToShow(newItem);
                                setOpenModal(true);
                              }}
                            >
                              Open
                            </Button>
                          </CardActions>
                        </Card>
                      )}
                    </Grid>
                  ))}
                </Grid>

                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    // marginTop: '20px',
                    justifyContent: 'center'
                  }}
                >
                  <Pagination
                    count={Math.ceil(firstAnswer.length / itemsPerPage)}
                    page={page}
                    onChange={handleChangePage}
                    sx={{
                      '& .MuiPaginationItem-page.Mui-selected': {
                        backgroundColor: '#2A3F90',
                        color: 'white'
                      },
                      '& .MuiPaginationItem-page.Mui-selected .MuiPaginationItem-pageNumber': {
                        color: 'white'
                      }
                    }}
                  />
                </Stack>
              </>
            )}
            {viewType === 'Listado' && (
              <>
                <Box
                  component="form"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    mb: 2
                  }}
                  noValidate
                  autoComplete="off"
                >
                  <Box sx={{ width: '100%', mb: 2 }}>
                    <TableContainer component={Paper}>
                      <StyledTable aria-label="customized table" size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <StyledTableCell style={{ minWidth: 'fit-content' }} />
                            <StyledTableCell align="left">Procesos</StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {firstAnswer
                            .slice(tablePage * rowsPerPage, tablePage * rowsPerPage + rowsPerPage)
                            .map(row => (
                              <StyledTableRow key={row.name}>
                                <StyledTableCell component="th" scope="row">
                                  {row.name}
                                </StyledTableCell>
                                <Button
                                  size="small"
                                  aria-label="centrar"
                                  style={{
                                    color: '#2A3F90',
                                    padding: '4px 8px', // Ajusta el espaciado interno del botón
                                    borderRadius: 4, // Agrega bordes redondeados al botón
                                    fontSize: 12, // Ajusta el tamaño de la fuente del botón
                                    fontWeight: 'bold' // Haz que el texto del botón sea más audaz
                                  }}
                                  onClick={() => {
                                    const newVariables = {};
                                    Object.keys(row.variables).forEach(key => {
                                      const variable = row.variables[key];
                                      newVariables[key] = {
                                        ...variable,
                                        input: variable.example
                                      };
                                    });
                                    const newItem = { ...row, variables: newVariables };
                                    setValueToShow(newItem);
                                    setOpenModal(true);
                                  }}
                                >
                                  Open
                                </Button>
                              </StyledTableRow>
                            ))}
                        </TableBody>
                      </StyledTable>
                    </TableContainer>
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    <TablePagination
                      rowsPerPageOptions={[10]}
                      component="div"
                      count={firstAnswer.length}
                      rowsPerPage={rowsPerPage}
                      page={tablePage}
                      onPageChange={handleChangeTablePage}
                    />
                  </Box>
                </Box>
              </>
            )}
          </>
        )}
      </Box>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        style={{
          position: 'absolute',
          bgcolor: 'white',
          top: '0%',
          left: '0%'
        }}
      >
        {body}
      </Modal>
    </>
  );
};

ISLComponent.defaultProps = {
  MapProps: {},
  isFullScreen: false
};
ISLComponent.propTypes = {
  isFullScreen: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  MapProps: PropTypes.object
};

export default ISLComponent;
